// Public runtime configuration. Values come from Vite env at build time
// (VITE_* vars). Visible in bundled JS — never put secrets here.
//
// E2E mode: when VITE_E2E_DEV_TOKEN is set the SPA short-circuits the OIDC
// flow (see auth/oidc-config.ts + main.tsx). The auth issuer and client_id
// values are still required by react-oidc-context for sessionStorage keying,
// but they don't have to point at a real Zitadel — fake placeholders are
// fine because the library never calls the discovery endpoint.

const apiURL = import.meta.env.VITE_API_URL;
const e2eDevToken = import.meta.env.VITE_E2E_DEV_TOKEN as string | undefined;

// Apex under which default-tier club subdomains live (e.g. "sportadmin.io"
// → "<websiteSlug>.sportadmin.io"). Used to build iframe-able preview
// URLs that route via the public-site host instead of the API host so
// the SPA's CSP frame-src can stay tight (`https://*.sportadmin.io`)
// and the API can keep `frame-ancestors 'none'`. Empty disables host-
// based previews and the SPA falls back to the legacy `/c/<slug>` path
// on the API host (broken in prod by the API's frame-ancestors lock).
const publicSitesBaseDomain =
  (import.meta.env.VITE_PUBLIC_SITES_BASE_DOMAIN as string | undefined) ?? '';

// Universal app URL (the Expo PWA at app.sportadmin.io). Used by the
// Settings → App preview page for both the iframe src and the
// "open in new tab" button. Driven entirely by VITE_APP_URL so dev
// doesn't accidentally iframe production — prod sets it in
// .env.production, dev leaves it unset (preview shows the placeholder)
// unless the engineer also runs the Expo web dev server and points
// VITE_APP_URL=http://localhost:8081.
const appURL = (import.meta.env.VITE_APP_URL as string | undefined) ?? '';

// Signup-portal base URL (the portal SPA at join.sportadmin.io). Parallels
// the backend's SPORTADMIN_GO_PORTAL_BASE_URL. When set, admin pages that
// link out to public signup land users directly on the portal
// (`<portalBaseURL>/o/<orgSlug>`) instead of bouncing through the legacy
// `/c/<slug>/signup` 301. Empty in dev unless local-bootstrap wires the
// portal host — callers then fall back to the legacy URL.
const portalBaseURL = (
  import.meta.env.VITE_PORTAL_BASE_URL as string | undefined
)?.replace(/\/+$/, '') ?? '';

const authIssuer = import.meta.env.VITE_AUTH_ISSUER ?? (e2eDevToken ? 'http://e2e.invalid' : '');
const authClientID = import.meta.env.VITE_AUTH_CLIENT_ID ?? (e2eDevToken ? 'e2e-fake-client' : '');

// CDN base for public media reads (logos, cover images, news heroes).
// In dev the API serves /storage/<key> directly, so the default here
// points back at the API URL. In prod, set VITE_CDN_BASE to
// "https://cdn.sportadmin.io" so previews go through the CDN.
// `||` (not `??`) so a present-but-blank VITE_CDN_BASE= falls back too —
// it leaks into Vite from the shared root .env via process.env, and `??`
// would let the empty string through, making publicURL() emit relative
// "/<key>" paths that resolve against the dev origin (5173) and 404.
const cdnBase = (import.meta.env.VITE_CDN_BASE as string | undefined)?.trim()
  || `${apiURL}/storage`;

// Sentry DSN for browser error/replay capture. Empty = soft-disable, the
// init helper becomes a no-op and the feedback feature still works (the
// issue body's sentry.* keys just ship empty). Public-by-design — DSNs
// belong in the bundle.
// SportAdmin Go's own legal pages, linked from the public signup-portal
// footer. The portal is platform-operated and SportAdmin Go is the data
// processor for portal signups, so these are the platform's terms + privacy
// policy — the club's signup-specific terms are captured separately as the
// per-team consent text. Defaults point at the marketing site; override per
// env (VITE_LEGAL_*_URL) if the live paths differ.
const legalTermsURL =
  (import.meta.env.VITE_LEGAL_TERMS_URL as string | undefined)?.trim() ||
  'https://sportadmin.io/villkor';
const legalPrivacyURL =
  (import.meta.env.VITE_LEGAL_PRIVACY_URL as string | undefined)?.trim() ||
  'https://sportadmin.io/integritetspolicy';

// Browser-restricted Google Maps JS key for the portal's map mode (the backend
// calls the same key SPORTADMIN_GO_WEB_FRONTEND_GOOGLE_API_KEY). Empty =
// soft-disable: the list view is unaffected and the map tab shows an
// "unavailable" message instead of a broken map. Public-by-design (it ships in
// the bundle) — lock it down with an HTTP-referrer restriction in GCP.
const googleMapsApiKey =
  (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() ?? '';

const sentryDSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? '';
const sentryEnvironment =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  (import.meta.env.MODE ?? 'development');
const sentryRelease = (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ?? '';

if (!apiURL) throw new Error('VITE_API_URL is not set');
if (!authIssuer) throw new Error('VITE_AUTH_ISSUER is not set');
if (!authClientID) throw new Error('VITE_AUTH_CLIENT_ID is not set');

export const config = {
  apiURL,
  appURL,
  authIssuer,
  authClientID,
  cdnBase,
  publicSitesBaseDomain,
  portalBaseURL,
  legalTermsURL,
  legalPrivacyURL,
  googleMapsApiKey,
  e2eDevToken: e2eDevToken ?? null,
  sentryDSN,
  sentryEnvironment,
  sentryRelease,
} as const;
